import React from 'react';

// A deliberately small markdown subset that covers what lawyers actually
// type in chat: **bold**, *italic*, `inline code`, ```code blocks```,
// > blockquote, and bullet lists. No tables, no images, no headings — the
// goal is to read fast, not to be a CMS.
//
// Combined with the existing @-mention pill renderer so both formatting
// and mentions coexist in one pass.

// Order matters: code spans must be tokenised before bold/italic so we
// don't try to re-format code body.
const INLINE_TOKEN_RE = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*|_[^_\n]+_|@\[[^\]]+\]\([0-9a-f-]{36}\))/gi;
const MENTION_RE      = /^@\[([^\]]+)\]\(([0-9a-f-]{36})\)$/i;

function renderInline(text, currentUserId, key) {
  if (!text) return null;
  const parts = [];
  let last = 0;
  let m;
  const re = new RegExp(INLINE_TOKEN_RE.source, 'gi');
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**') && tok.endsWith('**') && tok.length >= 4) {
      parts.push(<strong key={parts.length}>{tok.slice(2, -2)}</strong>);
    } else if ((tok.startsWith('*') && tok.endsWith('*')) || (tok.startsWith('_') && tok.endsWith('_'))) {
      parts.push(<em key={parts.length}>{tok.slice(1, -1)}</em>);
    } else if (tok.startsWith('`') && tok.endsWith('`')) {
      parts.push(
        <code key={parts.length} className="px-1 py-0.5 rounded bg-slate-200/70 dark:bg-slate-700/70 text-[0.85em] font-mono">
          {tok.slice(1, -1)}
        </code>
      );
    } else if (MENTION_RE.test(tok)) {
      const mm = tok.match(MENTION_RE);
      const isMe = mm[2] === currentUserId;
      parts.push(
        <span
          key={parts.length}
          className={`px-1 rounded font-medium ${
            isMe
              ? 'bg-amber-200 dark:bg-amber-500/30 text-amber-900 dark:text-amber-100'
              : 'bg-indigo-100 dark:bg-indigo-500/30 text-indigo-700 dark:text-indigo-200'
          }`}
        >
          @{mm[1]}
        </span>
      );
    } else {
      parts.push(tok);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <React.Fragment key={key}>{parts}</React.Fragment>;
}

/**
 * Render a message body with markdown subset + mention pills.
 *
 * Block-level features:
 *   - Triple-backtick fenced code blocks
 *   - `>` blockquote lines
 *   - `- ` or `* ` bullet list lines (consecutive lines collapse into a <ul>)
 * Other lines render as paragraphs preserving manual line breaks.
 */
export default function RichText({ body, currentUserId }) {
  if (!body) return null;

  // Split out fenced code blocks first; they should never be parsed as
  // markdown internally.
  const segments = [];
  const fenceRe = /```([\s\S]*?)```/g;
  let cursor = 0;
  let m;
  while ((m = fenceRe.exec(body))) {
    if (m.index > cursor) segments.push({ kind: 'text', value: body.slice(cursor, m.index) });
    segments.push({ kind: 'code', value: m[1].replace(/^\n+|\n+$/g, '') });
    cursor = m.index + m[0].length;
  }
  if (cursor < body.length) segments.push({ kind: 'text', value: body.slice(cursor) });

  const out = [];
  segments.forEach((seg, segIdx) => {
    if (seg.kind === 'code') {
      out.push(
        <pre
          key={`c-${segIdx}`}
          className="my-1 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-[0.85em] font-mono whitespace-pre-wrap overflow-x-auto"
        >
          {seg.value}
        </pre>
      );
      return;
    }
    // Text segment — split by lines and group bullet lists.
    const lines = seg.value.split('\n');
    let pendingList = [];
    const flushList = () => {
      if (!pendingList.length) return;
      out.push(
        <ul key={`l-${segIdx}-${out.length}`} className="my-1 ml-5 list-disc space-y-0.5">
          {pendingList.map((ln, li) => (
            <li key={li}>{renderInline(ln, currentUserId, `lt-${li}`)}</li>
          ))}
        </ul>
      );
      pendingList = [];
    };

    lines.forEach((rawLine, lineIdx) => {
      const line = rawLine ?? '';
      const trimmed = line.trimStart();
      if (/^[-*]\s+/.test(trimmed)) {
        pendingList.push(trimmed.replace(/^[-*]\s+/, ''));
        return;
      }
      flushList();
      if (/^>\s?/.test(trimmed)) {
        out.push(
          <blockquote
            key={`bq-${segIdx}-${lineIdx}`}
            className="my-1 pl-3 border-l-2 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 italic"
          >
            {renderInline(trimmed.replace(/^>\s?/, ''), currentUserId, `bq-${lineIdx}`)}
          </blockquote>
        );
        return;
      }
      // Plain line
      if (line === '') {
        out.push(<br key={`br-${segIdx}-${lineIdx}`} />);
        return;
      }
      out.push(
        <span key={`p-${segIdx}-${lineIdx}`}>
          {renderInline(line, currentUserId, `p-${lineIdx}`)}
          {lineIdx < lines.length - 1 ? <br /> : null}
        </span>
      );
    });
    flushList();
  });

  return <>{out}</>;
}

// Hint text for the composer footer so users discover the formatting.
export const FORMATTING_HINT = '**bold**, *italic*, `code`, > quote, - list';
